import upperFirst from 'lodash/upperFirst';
import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import resourcesToBackend from 'i18next-resources-to-backend';

import enTranslation from 'locales/en/translation.json';

// Default to English; language detection is opt-in via enableI18nDetection()
// so that the i18n rollout can be gated behind a feature flag.
//
// Translation key strategy: we use the English source string as the i18n key
// (keySeparator: false, nsSeparator: false). Locale files must match the English
// string exactly, including any Trans `<tag>{{var}}</tag>` markup and `{{var}}`
// placeholders. Any drift (whitespace, punctuation, tag rename, placeholder
// rename) silently breaks the lookup and falls back to the English source — no
// build error. When editing a phrase, update every locale JSON to match the
// new exact string. Pluralization uses i18next CLDR plural suffixes
// (`key_one`, `key_other`, `key_few`, ...) via `t(key, { count })`, not manual
// ternaries — see session_ended_item.jsx / custom_attribute_change_item.jsx.
// Only the default (en) catalog ships in the main bundle; other locales are
// code-split and fetched on demand when the language changes. Static import
// strings (not a template path) so both webpack and vite can split the chunks.
const lazyCatalogs = {
  es: () => import('locales/es/translation.json'),
};

i18n
  .use(
    resourcesToBackend(language => {
      const load = lazyCatalogs[language];
      // Languages without a lazy catalog (e.g. en, already bundled) merge nothing.
      return load ? load().then(module => module.default || module) : Promise.resolve({});
    })
  )
  .use(initReactI18next)
  .init({
    lng: 'en',
    fallbackLng: 'en',
    supportedLngs: ['en', 'es'],
    keySeparator: false,
    nsSeparator: false,
    partialBundledLanguages: true,
    resources: {
      en: { translation: enTranslation },
    },
    interpolation: {
      escapeValue: false,
    },
    react: {
      useSuspense: false,
    },
  });

let detectionEnabled = false;
export function enableI18nDetection() {
  if (detectionEnabled) return;
  detectionEnabled = true;

  const detector = new LanguageDetector(i18n.services, {
    order: ['localStorage', 'navigator'],
    lookupLocalStorage: 'gladly-language',
    caches: ['localStorage'],
  });
  const detected = detector.detect();
  if (detected && detected !== i18n.language) {
    i18n.changeLanguage(detected);
  }
}

i18n.services.formatter.add('upperFirst', upperFirst);

// Set document direction and language attribute on language change
i18n.on('languageChanged', lng => {
  if (typeof document === 'undefined') return;
  const rtlLanguages = ['ar', 'he', 'fa', 'ur'];
  document.documentElement.dir = rtlLanguages.includes(lng) ? 'rtl' : 'ltr';
  document.documentElement.lang = lng;
});

if (typeof window !== 'undefined') {
  window.i18n = i18n;
}

export default i18n;
